import { NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAdmin } from "../context/AdminAuth";
import {
  LayoutDashboard, Package, FolderTree, Layers, LayoutTemplate, ShoppingCart,
  Users, Ticket, Mail, Image, Settings, FileText, LogOut, Menu, X, ExternalLink,
  Boxes, Star,
} from "lucide-react";

const LINKS = [
  { to: "/admin", label: "Overview", icon: LayoutDashboard, end: true },
  { to: "/admin/products", label: "Products", icon: Package },
  { to: "/admin/inventory", label: "Inventory", icon: Boxes },
  { to: "/admin/categories", label: "Categories", icon: FolderTree },
  { to: "/admin/collections", label: "Collections", icon: Layers },
  { to: "/admin/homepage", label: "Homepage", icon: LayoutTemplate },
  { to: "/admin/content", label: "Content", icon: FileText },
  { to: "/admin/orders", label: "Orders", icon: ShoppingCart },
  { to: "/admin/reviews", label: "Reviews", icon: Star },
  { to: "/admin/customers", label: "Customers", icon: Users },
  { to: "/admin/coupons", label: "Coupons", icon: Ticket },
  { to: "/admin/newsletter", label: "Newsletter", icon: Mail },
  { to: "/admin/media", label: "Media", icon: Image },
  { to: "/admin/settings", label: "Settings", icon: Settings },
];

export default function AdminLayout({ children }) {
  const { logout, user } = useAdmin();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const Nav = () => (
    <nav className="flex-1 overflow-y-auto py-2">
      {LINKS.map((l) => (
        <NavLink key={l.to} to={l.to} end={l.end} onClick={() => setOpen(false)}
          data-testid={`admin-nav-${l.label.toLowerCase()}`}
          className={({ isActive }) =>
            `flex items-center gap-3 px-5 py-2.5 text-sm transition-colors ${isActive ? "text-white bg-zinc-900 border-l-2 border-accent-ss" : "text-zinc-400 hover:text-white hover:bg-zinc-900/50 border-l-2 border-transparent"}`}
          style={({ isActive }) => (isActive ? { borderColor: "var(--ss-accent)" } : {})}>
          <l.icon size={17} /> {l.label}
        </NavLink>
      ))}
    </nav>
  );

  return (
    <div className="min-h-screen bg-[#09090b] text-zinc-100 flex">
      {/* desktop sidebar */}
      <aside className="hidden lg:flex w-60 shrink-0 flex-col border-r border-zinc-800 fixed inset-y-0">
        <div className="px-5 py-5 border-b border-zinc-800">
          <span className="font-display text-3xl tracking-widest text-white">STREETSINS</span>
          <p className="text-[10px] uppercase tracking-widest text-zinc-600">Admin</p>
        </div>
        <Nav />
        <div className="border-t border-zinc-800 p-3 space-y-1">
          <a href="/" target="_blank" rel="noreferrer" className="flex items-center gap-2 px-2 py-2 text-xs text-zinc-400 hover:text-white"><ExternalLink size={14} /> View Store</a>
          <button onClick={async () => { await logout(); navigate("/admin"); }} className="flex items-center gap-2 px-2 py-2 text-xs text-zinc-400 hover:text-white w-full" data-testid="admin-logout-btn"><LogOut size={14} /> Logout</button>
        </div>
      </aside>

      {/* mobile */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="w-64 bg-[#09090b] border-r border-zinc-800 flex flex-col">
            <div className="px-5 py-5 border-b border-zinc-800 flex items-center justify-between">
              <span className="font-display text-2xl tracking-widest text-white">STREETSINS</span>
              <button onClick={() => setOpen(false)}><X size={20} /></button>
            </div>
            <Nav />
            <button onClick={async () => { await logout(); navigate("/admin"); }} className="flex items-center gap-2 px-5 py-3 text-xs text-zinc-400 border-t border-zinc-800"><LogOut size={14} /> Logout</button>
          </div>
          <div className="flex-1 bg-black/60" onClick={() => setOpen(false)} />
        </div>
      )}

      <div className="flex-1 lg:ml-60 min-w-0">
        <header className="lg:hidden flex items-center justify-between px-4 py-3 border-b border-zinc-800 sticky top-0 bg-[#09090b] z-40">
          <button onClick={() => setOpen(true)}><Menu size={22} /></button>
          <span className="font-display text-2xl tracking-widest text-white">STREETSINS</span>
          <span className="text-xs text-zinc-500">{user?.email?.split("@")[0]}</span>
        </header>
        <main className="p-4 sm:p-8 max-w-7xl">{children}</main>
      </div>
    </div>
  );
}
